<template>
    <div class="animated fadeIn">
        <div>
            <b-card>
                <div slot="header">
                    <strong style="margin-left: 5px">Project Details</strong>
                </div>

                <div role="tablist" class="tabbed-form">
                    <b-card no-body class="mb-1">
                        <b-card-header @click="toggleStep(0)" header-tag="header" class="p-1 form-step-name" role="tab">
                            <img src="../../../assets/gcp-icons/step1.png" />
                            {{ tabs[0] }}
                        </b-card-header>
                        <b-collapse v-model="tabOpen[0]" visible role="tabpanel">
                            <b-card-body>
                                <b-form class="step-form">
                                    <b-form-group label="Project name" :label-cols="2" :horizontal="true">
                                        <b-row>
                                            <b-col md="8">
                                                <b-form-input
                                                    v-model="$v.formData.name.$model"
                                                    type="text"
                                                    name="Project name"
                                                    placeholder="Enter your project name"
                                                ></b-form-input>
                                                <Feedback
                                                    :state="validateRef('formData.name', 0)"
                                                    invalid="This field is required and needs to be 25 characters at most"
                                                    valid="Project name is valid"
                                                ></Feedback>
                                            </b-col>
                                        </b-row>
                                    </b-form-group>

                                    <b-form-group label="Domain" :label-cols="2" :horizontal="true">
                                        <b-row>
                                            <b-col md="8">
                                                <v-select
                                                    class="text-nowrap"
                                                    v-model="$v.formData.domain.$model"
                                                    placeholder="Select Domain"
                                                    :options="domains"
                                                    id="domains-multiselect"
                                                    disabled
                                                ></v-select>
                                                <Feedback
                                                    :state="validateRef('formData.domain', 0)"
                                                    invalid="This field is required"
                                                    valid="Domain is valid"
                                                ></Feedback>
                                            </b-col>
                                        </b-row>
                                    </b-form-group>

                                    <b-form-group label="Append head" :label-cols="2" :horizontal="true">
                                        <b-row>
                                            <b-col md="8">
                                                <prism-editor
                                                    v-model="$v.formData.head.$model"
                                                    :code="formData.head"
                                                    language="html"
                                                ></prism-editor>
                                            </b-col>
                                        </b-row>
                                    </b-form-group>

                                    <b-form-group label="Append body" :label-cols="2" :horizontal="true">
                                        <b-row>
                                            <b-col md="8">
                                                <prism-editor
                                                    v-model="$v.formData.body.$model"
                                                    :code="formData.body"
                                                    language="html"
                                                ></prism-editor>
                                            </b-col>
                                        </b-row>
                                    </b-form-group>

                                    <Feedback :state="projectError.state" :invalid="projectError.message"></Feedback>

                                    <b-row>
                                        <b-col>
                                            <Feedback
                                                v-if="checkPage[0]"
                                                :state="validateRef('formData.name', 0)"
                                                invalid="You need to finish this step before accessing the next one"
                                                valid="This step is complete"
                                            ></Feedback>
                                            <b-button
                                                @click="updateProject"
                                                :disabled="checkPage[0] && $v.formData.name.$invalid"
                                                variant="primary"
                                            >
                                                <i class="icon-check"></i> Finish
                                            </b-button>
                                        </b-col>
                                    </b-row>
                                </b-form>
                            </b-card-body>
                        </b-collapse>
                    </b-card>
                </div>
            </b-card>
        </div>
        <Loading :loading="$store.state.loading.processing" :text="$store.state.loading.text"></Loading>
    </div>
</template>

<script>
import Vue from 'vue'

import {validationMixin} from 'vuelidate'
import {required, maxLength} from 'vuelidate/lib/validators'
import {mapGetters} from 'vuex'
import PrismEditor from 'vue-prism-editor'
import 'prismjs'
import 'prismjs/themes/prism.css'

import Loading from '@/components/loading.vue'
import Feedback from '@/components/Feedback'
import {filterDirty} from '@/shared/filterDirty.js'

export default {
    data() {
        return {
            tabs: ['Project settings'],
            tabIndex: 0,
            formData: {
                name: '',
                domain: '',
                head: '',
                body: '',
            },
            projectError: {
                state: null,
                message: null,
            },
            domains: [],
        }
    },
    computed: {
        ...mapGetters({
            activeProject: 'project/active',
        }),
        tabOpen() {
            return this.tabs.map((tab, index) => (index === this.tabIndex ? true : false))
        },
    },
    components: {
        Loading,
        Feedback,
        PrismEditor,
    },
    mixins: [validationMixin],
    validations: {
        formData: {
            name: {
                required,
                maxLength: maxLength(25),
            },
            domain: {
                required,
            },
            head: {},
            body: {},
        },
    },
    created() {
        this.fetchDomains()
        this.fetchProject()
        this.checkPage = this.tabs.map(el => false)
    },
    methods: {
        async fetchDomains() {
            this.domains = await this.$store.dispatch('project/fetchDomains')
        },

        toggleStep(step) {
            this.tabIndex = step
            this.$forceUpdate()
        },

        async fetchProject() {
            const {id} = this.$route.params
            this.project = await this.$store.dispatch('project/fetchProject', id)

            this.formData = {
                name: this.project.name,
                domain: this.project.domain,
                head: this.project.head,
                body: this.project.body,
            }

            this.fetchedFormData = Object.assign({}, this.formData)
            this.$nextTick(() => {
                this.$v.$reset()
            })
        },

        updateProject(event) {
            const formDirty = filterDirty(this.formData, this.$v.formData, this.fetchedFormData)
            if (Object.keys(formDirty).length > 0) {
                const data = {
                    projectData: {
                        ...formDirty,
                    },
                    id: this.project.id,
                }
                this.$store.commit('loading/PROCESSING', `Updating the Project...`)
                this.$store
                    .dispatch('project/updateProject', data)
                    .then(project => {
                        this.$store.commit('loading/PROCESSED')
                        this.$router.push({
                            path: '/iam/projects',
                        })
                    })
                    .catch(exception => {
                        this.projectError.state = false
                        this.projectError.message = exception.response.data
                        this.$store.commit('loading/PROCESSED')
                    })
            } else {
                this.$router.push({
                    path: '/iam/projects',
                })
            }
        },

        validateRef(ref, page) {
            const keys = ref.split('.')
            const validateField = keys.reduce((a, c) => {
                return a[c]
            }, this.$v)

            if (this.checkPage[page]) return !validateField.$invalid
            return validateField.$dirty ? !validateField.$invalid : null
        },
    },
}
</script>

<style lang="scss">
.prism-editor__code {
    background: white !important;
    border: 1px solid #e8e8e8;
    border-radius: 5px;
}
</style>
